import { Button } from '../../../../../components/Clickables/Buttons/Button';
import { InternalLink } from '../../../../../components/Clickables/InternalLink/InternalLink';
import { IFAQData } from '../../../../../components/FAQ/FAQ';
import { FishingReport } from '../../../../../components/FishingReportsGrid/components/FishingReportCard';
import { LatLng } from '../../../../../components/LatLng/LatLng';
import { getLocationLinksString } from '../../../../../components/LocationLinks/getLocationLinksString';
import { LocationLinks } from '../../../../../components/LocationLinks/LocationLinks';
import { addDefiniteArticleToWaterName } from '../../../../../helpers/FishingWater/addDefiniteArticleToWaterName';
import { addIndefiniteArticleToWaterType } from '../../../../../helpers/FishingWater/addIndefiniteArticleToWaterType';
import { getEdgesFromConnection } from '../../../../../helpers/graphql';
import { getSpeciesUrl, getUserUrl } from '../../../../../helpers/urls';
import {
  GetFishingWater_fishingWater_country,
  GetFishingWater_fishingWater_region,
  GetFishingWaterTopSpecies_fishingWater_topSpeciesSummary,
  GetFishingWaterTopSpecies_fishingWater_topSpeciesSummary_edges,
  GetFishingWaterTopSpecies_fishingWater_topSpeciesSummary_edges_node,
  WaterTypes,
} from '../../../../../interfaces/graphql';

interface IProps {
  country: GetFishingWater_fishingWater_country | null;
  descriptionOfWaterType: WaterTypes;
  fishingWaterName: string;
  handleCTAClick: () => void;
  latitude: number;
  longitude: number;
  region: GetFishingWater_fishingWater_region | null;
  recentCatchPostList: FishingReport[];
  topSpeciesSummary?: GetFishingWaterTopSpecies_fishingWater_topSpeciesSummary;
}

const NUMBER_OF_FISHING_REPORTS_TO_SHOW = 5;

export const getFAQData = ({
  country,
  descriptionOfWaterType,
  fishingWaterName,
  handleCTAClick,
  latitude,
  longitude,
  region,
  recentCatchPostList,
  topSpeciesSummary,
}: IProps): IFAQData[] => {
  const countryName = country?.countryName;
  const countryId = country?.id;
  const speciesEdges = getEdgesFromConnection<
    GetFishingWaterTopSpecies_fishingWater_topSpeciesSummary_edges_node,
    GetFishingWaterTopSpecies_fishingWater_topSpeciesSummary_edges
  >(topSpeciesSummary);

  return [
    {
      id: '1',
      title: `📍 Where is ${addDefiniteArticleToWaterName(fishingWaterName, descriptionOfWaterType)} located?`,
      body: (
        <>
          {`${addDefiniteArticleToWaterName(fishingWaterName, descriptionOfWaterType)} is ${addIndefiniteArticleToWaterType(descriptionOfWaterType)} located in `}
          <LocationLinks
            regionId={region?.id}
            regionName={region?.name}
            countryId={countryId}
            countryName={countryName}
            className="m-0 text-left"
            LinkClassName="body-text-sm"
            textAs="span"
            textVariant="md"
          />
          {`. Its coordinates are: `}{' '}
          <LatLng textVariant="sm" className="inline" latitude={latitude} longitude={longitude} />
          {'.'}
        </>
      ),
      bodyString: `${addDefiniteArticleToWaterName(fishingWaterName, descriptionOfWaterType)} is ${addIndefiniteArticleToWaterType(descriptionOfWaterType)} located in ${getLocationLinksString({ regionId: region?.id, regionName: region?.name, countryId: countryId, countryName: countryName })}. Its coordinates are: ${latitude}, ${longitude}.`,
    },
    {
      id: '2',
      title: `🎣 Where on ${addDefiniteArticleToWaterName(fishingWaterName, descriptionOfWaterType)} is it best to fish?`,
      body: (
        <>
          {`Find the best fishing spots on ${addDefiniteArticleToWaterName(fishingWaterName, descriptionOfWaterType)} by looking at where other anglers have caught fish. This can easily be done in the `}
          <Button
            onClick={handleCTAClick}
            size="sm"
            className="body-text-sm font-semibold text-primary"
            variant="textNone"
          >
            Fishbrain app
          </Button>
          {` where it is also possible to see big fish potential and how good fishing is right now along with Navionics depth maps.`}
        </>
      ),
      bodyString: `Find the best fishing spots on ${addDefiniteArticleToWaterName(fishingWaterName, descriptionOfWaterType)} by looking at where other anglers have caught fish. This can easily be done in the Fishbrain app where it is also possible to see big fish potential and how good fishing is right now along with Navionics depth maps.`,
    },
    ...(speciesEdges.length > 0
      ? [
          {
            id: '3',
            title: `🐟 What species are in ${addDefiniteArticleToWaterName(fishingWaterName, descriptionOfWaterType)}?`,
            body: (
              <>
                {`The most common species in ${addDefiniteArticleToWaterName(fishingWaterName, descriptionOfWaterType)} are: `}
                <ul className="m-0 list-disc px-4 py-2">
                  {speciesEdges.map(({ node }) => {
                    return (
                      <li className="body-text-sm m-0" key={`species-${node._id}`}>
                        <InternalLink
                          to={getSpeciesUrl(node._id, node.displayName)}
                          className="body-text-sm text-primary"
                        >
                          {node.displayName}
                        </InternalLink>
                        {` - ${node.anglers.topAnglers?.totalCount} members reported to have caught this fish`}
                      </li>
                    );
                  })}
                </ul>
              </>
            ),
            bodyString: `The most common species in ${addDefiniteArticleToWaterName(fishingWaterName, descriptionOfWaterType)} are: <ul>${speciesEdges
              .map(
                ({ node }) =>
                  `<li><a href="${getSpeciesUrl(node._id, node.displayName)}">${node.displayName}</a> - ${node.anglers.topAnglers?.totalCount} members reported to have caught this fish</li>`,
              )
              .join('')}</ul>.`,
          },
        ]
      : []),
    ...(recentCatchPostList.length > 0
      ? [
          {
            id: '4',
            title: `📢 What are the latest ${fishingWaterName} fishing reports?`,
            body: (
              <>
                {`The latest ${fishingWaterName} fishing reports are: `}
                <ul className="m-0 list-disc px-4 py-2">
                  {recentCatchPostList.slice(0, NUMBER_OF_FISHING_REPORTS_TO_SHOW).flatMap(post => {
                    const userNickname = post.user.nickname;
                    const speciesId = post.catch?.species?._id;
                    const speciesName = post.catch?.species?.displayName;
                    if (!userNickname || !speciesId || !speciesName) {
                      return [];
                    }
                    return (
                      <li className="body-text-sm m-0" key={`post-${post._id}`}>
                        <InternalLink
                          to={getUserUrl(userNickname)}
                          className="body-text-sm text-primary"
                        >
                          {`@${userNickname}`}
                        </InternalLink>
                        {` reported a nice `}
                        <InternalLink
                          to={getSpeciesUrl(speciesId, speciesName)}
                          className="body-text-sm text-primary"
                        >
                          {speciesName}
                        </InternalLink>
                        {`.`}
                      </li>
                    );
                  })}
                </ul>
              </>
            ),
            bodyString: `The latest ${fishingWaterName} fishing reports are: <ul>${recentCatchPostList
              .slice(0, NUMBER_OF_FISHING_REPORTS_TO_SHOW)
              .flatMap(post => {
                const userNickname = post.user.nickname;
                const speciesId = post.catch?.species?._id;
                const speciesName = post.catch?.species?.displayName;
                if (!userNickname || !speciesId || !speciesName) {
                  return [];
                }
                return `<li><a href="${getUserUrl(userNickname)}">@${userNickname}</a> reported a nice <a href="${getSpeciesUrl(
                  speciesId,
                  speciesName,
                )}">${speciesName}</a>.</li>`;
              })
              .join('')}</ul>.`,
          },
        ]
      : []),
  ];
};
